import { REPORT_PACKAGE_ORDER, reportLabels } from '../constants/reportLabels';
import type { ExportAvailability, PackageSummary, ReportContext, ReportPackage, ReportPackageKey } from '../types';

/** The report reflects the saved project, so unsaved edits block the export before anything else. */
export const canExport = (context: ReportContext | undefined, modified: boolean, isContextError = false): ExportAvailability => {
  if (modified) return { enabled: false, reason: reportLabels.unsaved_changes };
  if (!context) return { enabled: false, reason: isContextError ? reportLabels.context_error : reportLabels.context_loading };
  if (!context.exportable) return { enabled: false, reason: reportLabels.not_exportable };
  return { enabled: true, reason: null };
};

export const summarizePackages = (packages: Record<ReportPackageKey, ReportPackage>): PackageSummary => ({
  included: REPORT_PACKAGE_ORDER.filter((key) => packages[key]?.status === 'up_to_date'),
  stale: REPORT_PACKAGE_ORDER.filter((key) => packages[key]?.status === 'stale'),
  missing: REPORT_PACKAGE_ORDER.filter((key) => !packages[key] || packages[key].status === 'missing'),
});

/** Supports both `filename*=UTF-8''…` (RFC 5987) and plain `filename="…"`. */
export const parseContentDispositionFilename = (header: unknown): string | null => {
  if (typeof header !== 'string') return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      return null;
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/.exec(header);
  return plain ? plain[1].trim() : null;
};

export const triggerFileDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking right away can cancel the download in some browsers (Safari, Firefox).
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A datetime without offset is a UTC datetime from the backend, not a local one. */
const parseUtcDate = (iso: string): Date => new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);

export const formatReportDate = (iso: string): { local: string; utc: string } => {
  const date = parseUtcDate(iso);
  if (Number.isNaN(date.getTime())) return { local: iso, utc: '' };
  return {
    local: date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    utc: `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`,
  };
};
